/** @jsxImportSource @emotion/react */
import React, { ButtonHTMLAttributes, MouseEvent } from "react";
import { css } from "@emotion/react";
import { ReactNode, forwardRef } from "react";
import { MAP_TOOL_BUTTON } from "src/domains/shared/constants/mapToolButton";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  isDisabled?: boolean;
  onClick?: (e?: MouseEvent<HTMLButtonElement>) => void;
  isActive?: boolean;
  hasPopup?: boolean;
  popupText?: string;
}

const getStyledButtonStyle = (isDisabled: boolean, active?: boolean) => css`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${MAP_TOOL_BUTTON.SIZE_PX}px;
  height: ${MAP_TOOL_BUTTON.SIZE_PX}px;
  padding: 0;
  border: none;
  border-radius: 0;
  background: ${active ? MAP_TOOL_BUTTON.activeBackground : "transparent"};
  cursor: ${isDisabled ? "not-allowed" : "pointer"};
  opacity: ${isDisabled ? MAP_TOOL_BUTTON.DISABLED_OPACITY : 1};
  position: relative;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: ${active
      ? MAP_TOOL_BUTTON.activeHoverBackground
      : MAP_TOOL_BUTTON.hoverBackground};
    outline: none;
  }
`;

/**
 * 툴팁 요소를 가리키는 표지 속성 — 툴팁 요소는 `TOOLTIP_MARKER_PROPS` 를 펼쳐 이 속성을 달고,
 * `buttonContainerStyle` 의 호버 셀렉터도 이 상수로 조립한다. 툴팁(`buttonPopupStyle`)은 기본이
 * `visibility: hidden` 이라 두 자리가 갈라지면 타입·린트 신호 없이 이 버튼의 툴팁만 영영 숨은 채 남는다.
 */
const TOOLTIP_MARKER_ATTR = "data-tooltip";

const TOOLTIP_MARKER_PROPS = { [TOOLTIP_MARKER_ATTR]: true } as const;

const buttonContainerStyle = css`
  position: relative;

  &:hover [${TOOLTIP_MARKER_ATTR}] {
    visibility: visible;
  }
`;

const buttonPopupStyle = css`
  visibility: hidden;
  opacity: ${MAP_TOOL_BUTTON.TOOLTIP_OPACITY};
  font-size: ${MAP_TOOL_BUTTON.TOOLTIP_FONT_PX}px;
  font-weight: ${MAP_TOOL_BUTTON.TOOLTIP_FONT_WEIGHT};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${MAP_TOOL_BUTTON.TOOLTIP_RADIUS_PX}px;
  position: absolute;
  background-color: ${MAP_TOOL_BUTTON.tooltipBackground};
  height: ${MAP_TOOL_BUTTON.TOOLTIP_HEIGHT_PX}px;
  color: ${MAP_TOOL_BUTTON.tooltipColor};
  white-space: nowrap;
  padding: 0 ${MAP_TOOL_BUTTON.TOOLTIP_PADDING_X_PX}px;
  top: 50%;
  left: calc(100% + ${MAP_TOOL_BUTTON.TOOLTIP_OFFSET_PX}px);
  transform: translateY(-50%);
  box-shadow: ${MAP_TOOL_BUTTON.TOOLTIP_SHADOW};
  z-index: ${MAP_TOOL_BUTTON.TOOLTIP_Z_INDEX};
  pointer-events: none;

  &::before {
    content: "";
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: ${MAP_TOOL_BUTTON.TOOLTIP_ARROW_PX}px solid transparent;
    border-bottom: ${MAP_TOOL_BUTTON.TOOLTIP_ARROW_PX}px solid transparent;
    border-right: ${MAP_TOOL_BUTTON.TOOLTIP_ARROW_PX}px solid
      ${MAP_TOOL_BUTTON.tooltipBackground};
  }
`;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      hasPopup = false,
      popupText,
      children,
      onClick,
      isDisabled = false,
      isActive = false,
      ...props
    }: ButtonProps,
    ref
  ) => {
    const onClickBtn = (e: MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        onClick(e);
      }
    };

    return (
      <div css={buttonContainerStyle}>
        <button
          ref={ref}
          type="button"
          aria-label={popupText}
          onClick={onClickBtn}
          css={getStyledButtonStyle(isDisabled, isActive)}
          {...props}
        >
          {children}
        </button>
        {/* 3D 버튼과 같은 CSS 호버 방식이다 — 지도 컨트롤 위에서는 React 합성 마우스
            이벤트가 오지 않는 경우가 있어 상태로 여닫으면 툴팁이 뜨지 않는다. */}
        {hasPopup && (
          <div {...TOOLTIP_MARKER_PROPS} role="tooltip" css={buttonPopupStyle}>
            {popupText}
          </div>
        )}
      </div>
    );
  }
);

Button.displayName = "Button";
